/**
 * The deaf call, and the one silent redial that heals it (2026-09-21, ruling R-D1).
 *
 * His report: "I switch it on, it says hi Keshav, and when I talk it doesn't respond ... then I
 * have to refresh to make it work." About two calls in three. The evidence line proved the mic is
 * alive, the uplink clean and every packet acknowledged, and the vendor still produced no input
 * transcript and no delegation. A redial usually fixes it, so the line does the redial itself.
 *
 * EVERY GATE POSITIVE, and a missing number is doubt, not evidence. A wrong redial throws away a
 * live call and whatever was said on it, so this says "redial" only when it can name, from
 * numbers it actually has, that he WAS talking into a track that was on the wire and nobody
 * heard him. Absent is never treated as zero: it listens.
 *
 * The gates split by who knows what. The server owns `awaitingVoice` (its greeting is finished
 * and it is waiting) and `heard` (an input transcript or a delegation arrived); the browser owns
 * `voicedMs` and `sentLevel` (only it can see its own microphone and sender). Neither could judge
 * this alone, which is why the fact rides the state frame rather than a channel of its own.
 */
import { LINE_SPEECH_RMS } from "@bash/shared";

/** Under this the line is still settling: a greeting, a profile switch, the first packets. */
export const DEAF_MIN_OPEN_MS = 6_000;
/** He has to have SPOKEN. Under this he is a person listening, not a line that cannot hear. */
export const DEAF_VOICED_MS = 1_200;
/**
 * And he has to have STOPPED (R-D3). The vendor may only emit an input transcript when a turn
 * COMMITS, so "he has been talking for over a second and nothing came back" is the NORMAL shape
 * of someone mid-sentence. Waiting for this much continuous quiet after his last voiced poll is
 * what separates a line that cannot hear from a line that has not been given a turn yet — and a
 * redial dropped into the middle of a healthy long sentence would be worse than the bug.
 */
export const DEAF_QUIET_MS = 2_500;
/**
 * THE LADDER's first rung (2026-09-21, after the staging drill). A fake microphone was heard 12
 * of 12 on staging at three volumes, two of them quieter than his deaf call, so the vendor, the
 * server and the seed are all fine: what differs is what HIS browser captures. Every deaf call of
 * his reads device=builtin, because the chooser takes the built-in whenever the default input is
 * Bluetooth earbuds, and those calls carry energy the vendor cannot make into speech (rms 0.05,
 * 27 s voiced, and the only thing it transcribed in 23 s was a throat clear). So the cheapest
 * rung is not a dial at all: it is a DIFFERENT MICROPHONE on the same call, which costs him no
 * greeting and no pause.
 */
export const DEAF_MAX_MIC_SWAPS = 1;
/** One a call. Each dial costs him the greeting again, so a second would be a loop, not a heal. */
export const DEAF_MAX_REDIALS = 1;

export interface DeafFacts {
  /** How long this line has been open, in ms. */
  openMs: number;
  /** The SERVER's word: its greeting is finished and it is waiting to hear him. */
  awaitingVoice: boolean;
  /** Voiced ms since the greeting ended, or null where the browser measured none. */
  voicedMs: number | null;
  /** Continuous non-voiced ms since his last voiced poll, or null while he has never been voiced. */
  quietMs: number | null;
  /** The browser's own level for the track on the wire, or null where it reports none. */
  sentLevel: number | null;
  /** The server has heard him on this call: an input transcript or a delegation. */
  heard: boolean;
  /** The person has the mic off the air on purpose. */
  muted: boolean;
  /** A delegation is in flight: a redial would throw the work away. */
  working: boolean;
  /** Mic swaps this conversation has already spent on a deaf verdict. */
  micSwaps: number;
  /** Silent redials this conversation has already spent. */
  redials: number;
}

export type DeafVerdict =
  /** Nothing to do: either the line is fine, or nothing proves it is not. */
  | "listening"
  /** Deaf, and the cheapest rung is left: put another input on the air, same call. */
  | "swap"
  /** Deaf on the swapped input too: redial once, silently. */
  | "redial"
  /** Deaf after both: no further rung, say so instead. */
  | "give-up";

export function judgeDeaf(f: DeafFacts): DeafVerdict {
  // Heard once is heard for good; muted and working are his doing, not a fault.
  if (f.heard || f.muted || f.working) return "listening";
  // The server's greeting has to be out of the way before his silence means anything.
  if (!f.awaitingVoice || f.openMs < DEAF_MIN_OPEN_MS) return "listening";
  // Absent is doubt: a browser that measured none of these proves nothing either way.
  if (f.voicedMs === null || f.quietMs === null || f.sentLevel === null)
    return "listening";
  // He spoke, and it reached the wire. Both, or it is not this fault.
  if (f.voicedMs < DEAF_VOICED_MS || f.sentLevel < LINE_SPEECH_RMS)
    return "listening";
  // And he has STOPPED: mid-sentence, the vendor owes us nothing yet.
  if (f.quietMs < DEAF_QUIET_MS) return "listening";
  // The ladder, cheapest rung first: another microphone costs him nothing, a
  // dial costs him the greeting, and the sentence is the end of what we can do.
  if (f.micSwaps < DEAF_MAX_MIC_SWAPS) return "swap";
  return f.redials < DEAF_MAX_REDIALS ? "redial" : "give-up";
}
